import React, { useState, useEffect } from "react";
import { endPoints } from "../../constant/Environment";
import { addData } from "../../Utility/API";
import Toaster from "../../components/Toast";
import Loader from "../../components/Loader";
import { ErrorToast, SuccesToast } from "../../components/Toast/message";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Grid from "@mui/material/Grid";

const REWARD_TYPES = ["NONE", "SC", "GC"];

// The API returns dates as ISO-8601 carrying the server's offset. Convert to the
// "yyyy-MM-ddTHH:mm" a datetime-local input needs, in the admin's own timezone.
const toPickerValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return String(value).substring(0, 16);
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

// Picker value -> absolute instant, so the survey opens and closes at the moment
// the admin picked no matter which timezone anyone is in.
const toInstant = (value) => (value ? new Date(value).toISOString() : null);

const emptyForm = {
  title: "",
  description: "",
  question: "",
  option1: "",
  option2: "",
  option3: "",
  option4: "",
  iconUrl: "",
  rewardType: "NONE",
  rewardValue: "",
  startDate: "",
  endDate: "",
  active: true,
};

const CreateSurvey = ({ open, onClose, editData = {} }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  useEffect(() => {
    if (!open) return;
    if (editData?.id) {
      setForm({
        ...emptyForm,
        ...editData,
        rewardValue: editData.rewardValue ?? "",
        startDate: toPickerValue(editData.startDate),
        endDate: toPickerValue(editData.endDate),
      });
    } else {
      setForm(emptyForm);
    }
  }, [open, editData]);

  const submit = () => {
    if (!form.title.trim()) {
      ErrorToast("Title is required");
      return;
    }
    if (!form.question.trim()) {
      ErrorToast("Question is required");
      return;
    }
    if (!form.option1.trim() || !form.option2.trim()) {
      ErrorToast("At least two options are required");
      return;
    }
    if (form.rewardType !== "NONE" && !(Number(form.rewardValue) > 0)) {
      ErrorToast("Enter the reward value");
      return;
    }
    if (form.startDate && form.endDate && new Date(form.endDate) <= new Date(form.startDate)) {
      ErrorToast("End date must be after start date");
      return;
    }

    const payload = {
      ...form,
      rewardValue: form.rewardType === "NONE" ? 0 : Number(form.rewardValue),
      startDate: toInstant(form.startDate),
      endDate: toInstant(form.endDate),
    };
    if (editData?.id) payload.id = editData.id;

    const url = editData?.id ? endPoints.api.SURVEY_UPDATE : endPoints.api.SURVEY_ADD;
    setIsLoading(true);
    addData(url, payload)
      .then((res) => {
        setIsLoading(false);
        if (res?.data?.status === "success") {
          SuccesToast(res?.data?.message || "Survey saved");
          onClose();
        } else {
          ErrorToast(res?.data?.message || "Failed to save survey");
        }
      })
      .catch((err) => {
        setIsLoading(false);
        console.error(err);
        ErrorToast("Failed to save survey");
      });
  };

  const field = { width: "100%", marginTop: "16px" };

  return (
    <Dialog maxWidth="md" fullWidth open={open}>
      <DialogTitle sx={{ pr: 6 }}>
        {editData?.id ? "Edit Survey" : "Add Survey"}
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Toaster />
        {isLoading && <Loader spinner={true} visible={isLoading} />}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField label="Title" value={form.title} onChange={set("title")} sx={field} size="small" />
          </Grid>
          {/* Icon URL is set on Promotions -> Refer & Daily Claim instead: the tile
              reads that row's icon and it always wins over this one, so having the
              field here just gives two places to set one icon. Kept commented in
              case the survey ever needs its own icon independent of the config row.
          <Grid item xs={12} md={6}>
            <TextField label="Icon URL" value={form.iconUrl} onChange={set("iconUrl")} sx={field} size="small"
              helperText="Shown on the promotions card" />
          </Grid>
          */}

          <Grid item xs={12}>
            <TextField label="Description" value={form.description} onChange={set("description")}
              sx={field} size="small" multiline rows={2} />
          </Grid>

          <Grid item xs={12}>
            <TextField label="Question" value={form.question} onChange={set("question")}
              sx={field} size="small" helperText="The question players answer in the modal" />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField label="Option 1" value={form.option1} onChange={set("option1")} sx={field} size="small" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Option 2" value={form.option2} onChange={set("option2")} sx={field} size="small" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Option 3 (optional)" value={form.option3} onChange={set("option3")} sx={field} size="small" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Option 4 (optional)" value={form.option4} onChange={set("option4")} sx={field} size="small" />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField select label="Reward Type" value={form.rewardType} onChange={set("rewardType")}
              sx={field} size="small">
              {REWARD_TYPES.map((t) => (
                <MenuItem key={t} value={t}>{t === "NONE" ? "No reward" : t}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Reward Value" type="number" value={form.rewardValue} onChange={set("rewardValue")}
              sx={field} size="small" disabled={form.rewardType === "NONE"}
              helperText={form.rewardType === "NONE" ? "No reward for this survey" : `Credited once, on the player's first answer`} />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField label="Start Date" type="datetime-local" value={form.startDate} onChange={set("startDate")}
              sx={field} size="small" InputLabelProps={{ shrink: true }}
              helperText="Blank = live immediately" />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Shown Until" type="datetime-local" value={form.endDate} onChange={set("endDate")}
              sx={field} size="small" InputLabelProps={{ shrink: true }}
              helperText="Blank = no end date" />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />}
              label="Active"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={isLoading}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateSurvey;
